import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type {
  Block,
  BlockType,
  RowLayout,
  TemplateBlocks,
} from '@engage/types';
import { useToast } from '../../components/ui/Toast';
import { ImageUploadField } from '../../components/ui/ImageUploadField';
import { useAuthStore } from '../../stores/auth.store';
import {
  useTemplate,
  useUpdateTemplate,
  useCreateTemplate,
  usePreviewTemplate,
  usePreviewSamples,
  useSendTestTemplate,
} from '../../api/templates.api';
import {
  addBlock,
  changeRowLayout,
  defaultBlock,
  defaultRow,
  duplicateBlock,
  emptyTree,
  findBlock,
  moveBlock,
  moveRow,
  removeBlock,
  removeRow,
  updateBlock,
  updateRow,
  type BlockAddress,
} from './builder/blockTree';
import { MergeFieldInserter } from './builder/MergeFieldInserter';
import { LinkiActions } from './builder/LinkiActions';
import { CalendarSlotPicker } from './builder/CalendarSlotPicker';
import { COMMON_MERGE_TOKENS } from '../../lib/merge-tokens';

/* ── Screen — three-pane template builder ──
   Left: block list with reorder / settings selection
   Centre: live preview iframe + sample switcher + send-test
   Right: per-selection settings panel (block / row / template) */

const BLOCK_TYPES: BlockType[] = [
  'heading',
  'paragraph',
  'image',
  'button',
  'divider',
  'spacer',
  'calendar_slot',
  'job_card',
  'signature',
  'bullet_list',
  'detail_rows',
  'footer',
];

const ROW_LAYOUTS: RowLayout[] = ['1', '1-2', '1-3', '1-2-1', '1-3-1', 'banded-1-2-1'];

const SAVE_DEBOUNCE_MS = 1500;
const PREVIEW_DEBOUNCE_MS = 300;

export function TemplateBuilderPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id;
  const navigate = useNavigate();
  const { addToast } = useToast();
  const me = useAuthStore((s) => s.user);

  const { data: existing, isLoading } = useTemplate(id);
  const createMut = useCreateTemplate();
  const updateMut = useUpdateTemplate(id ?? '');
  const previewMut = usePreviewTemplate();
  const { data: samplesData } = usePreviewSamples();
  const sampleList = samplesData?.data ?? [];

  const [tree, setTree] = useState<TemplateBlocks>(() => emptyTree('New template'));
  const [name, setName] = useState('New template');
  const [selected, setSelected] = useState<BlockAddress | null>(null);
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [sampleId, setSampleId] = useState<string>('candidate_sample');
  const [previewHtml, setPreviewHtml] = useState<string>('');
  const [viewportWidth, setViewportWidth] = useState<600 | 360>(600);
  const [saveStatus, setSaveStatus] = useState<'saved' | 'saving' | 'idle' | 'error'>('idle');
  const [sendTestOpen, setSendTestOpen] = useState(false);

  useEffect(() => {
    if (existing) {
      setName(existing.name);
      if (existing.blocks) {
        setTree(existing.blocks);
      } else {
        setTree({
          settings: { subject: existing.subject, preheader: existing.preheader ?? undefined },
          rows: [
            { id: 'r_legacy', layout: '1', columns: [{ id: 'c_legacy', blocks: [{ id: 'p_legacy', type: 'paragraph', props: { html: existing.body_html || existing.body_text } }] }] },
          ],
        });
      }
    }
  }, [existing]);

  // Live preview — debounced render whenever the tree or sample changes.
  const previewTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (previewTimer.current) clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(() => {
      previewMut.mutate(
        { blocks: tree, sample_id: sampleId },
        { onSuccess: (out) => setPreviewHtml(out.html) },
      );
    }, PREVIEW_DEBOUNCE_MS);
    return () => {
      if (previewTimer.current) clearTimeout(previewTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tree, sampleId]);

  // Auto-save — only after the user has loaded an existing template.
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (isNew || !existing) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    setSaveStatus('idle');
    saveTimer.current = setTimeout(() => {
      setSaveStatus('saving');
      updateMut.mutate(
        { name, blocks: tree, subject: tree.settings.subject, preheader: tree.settings.preheader ?? null },
        {
          onSuccess: () => setSaveStatus('saved'),
          onError: () => setSaveStatus('error'),
        },
      );
    }, SAVE_DEBOUNCE_MS);
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tree, name]);

  const handleSave = async () => {
    setSaveStatus('saving');
    try {
      if (isNew) {
        const created = await createMut.mutateAsync({
          name,
          subject: tree.settings.subject,
          preheader: tree.settings.preheader ?? null,
          blocks: tree,
        });
        setSaveStatus('saved');
        navigate(`/templates/${created.id}/edit`, { replace: true });
      } else {
        await updateMut.mutateAsync({
          name,
          subject: tree.settings.subject,
          preheader: tree.settings.preheader ?? null,
          blocks: tree,
        });
        setSaveStatus('saved');
        addToast({ variant: 'success', title: 'Saved' });
      }
    } catch {
      setSaveStatus('error');
      addToast({ variant: 'danger', title: 'Save failed' });
    }
  };

  const handleSaveAsNew = async () => {
    try {
      const created = await createMut.mutateAsync({
        name: `${name} (copy)`,
        subject: tree.settings.subject,
        preheader: tree.settings.preheader ?? null,
        blocks: tree,
      });
      navigate(`/templates/${created.id}/edit`);
    } catch {
      addToast({ variant: 'danger', title: 'Save as new failed' });
    }
  };

  const handleExit = () => navigate('/templates');

  if (!isNew && isLoading) {
    return <div style={{ padding: 32 }}>Loading…</div>;
  }
  if (existing?.is_system) {
    return (
      <div style={{ padding: 32 }}>
        <p style={{ color: 'var(--color-danger, #e03131)' }}>
          System templates are read-only. Use "Customise" in the gallery to make an editable copy.
        </p>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 80px)', gap: 12 }}>
      <SaveBar
        name={name}
        onName={setName}
        saveStatus={saveStatus}
        onSave={handleSave}
        onSaveAsNew={handleSaveAsNew}
        onSendTest={() => setSendTestOpen(true)}
        onExit={handleExit}
      />

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '280px minmax(0, 1fr) 340px',
          gap: 12,
          flex: 1,
          minHeight: 0,
        }}
      >
        <BlockListPane
          tree={tree}
          selected={selected}
          selectedRowId={selectedRowId}
          onSelectBlock={(addr) => { setSelected(addr); setSelectedRowId(addr.rowId); }}
          onSelectRow={(rowId) => { setSelected(null); setSelectedRowId(rowId); }}
          onAddRow={(layout) => {
            // Build the row up front so we can auto-select it after add —
            // matches the Forms builder UX: adding a thing immediately
            // surfaces its settings on the right pane.
            const row = defaultRow(layout);
            setTree((t) => ({ ...t, rows: [...t.rows, row] }));
            setSelected(null);
            setSelectedRowId(row.id);
          }}
          onMoveRow={(rowId, dir) => setTree((t) => moveRow(t, rowId, dir))}
          onRemoveRow={(rowId) => {
            setTree((t) => removeRow(t, rowId));
            if (selectedRowId === rowId) setSelectedRowId(null);
            if (selected?.rowId === rowId) setSelected(null);
          }}
          onAddBlock={(rowId, colId, type) => {
            // Same auto-select-on-add UX as Forms: build the block first,
            // add it, then select so its settings appear immediately.
            const block = defaultBlock(type);
            setTree((t) => addBlock(t, rowId, colId, block));
            setSelected({ rowId, columnId: colId, blockId: block.id });
            setSelectedRowId(rowId);
          }}
          onMoveBlock={(addr, dir) => setTree((t) => moveBlock(t, addr, dir))}
          onRemoveBlock={(addr) => {
            setTree((t) => removeBlock(t, addr));
            if (selected?.blockId === addr.blockId) setSelected(null);
          }}
          onDuplicateBlock={(addr) => setTree((t) => duplicateBlock(t, addr))}
        />

        <PreviewPane
          html={previewHtml}
          viewportWidth={viewportWidth}
          onViewportWidth={setViewportWidth}
          samples={sampleList}
          sampleId={sampleId}
          onSampleId={setSampleId}
          loading={previewMut.isPending && !previewHtml}
        />

        <SettingsPanel
          tree={tree}
          selected={selected}
          selectedRowId={selectedRowId}
          onUpdateBlock={(addr, patch) => setTree((t) => updateBlock(t, addr, patch))}
          onUpdateRow={(rowId, patch) => setTree((t) => updateRow(t, rowId, patch))}
          onChangeRowLayout={(rowId, layout) => setTree((t) => changeRowLayout(t, rowId, layout))}
          onUpdateSettings={(patch) => setTree((t) => ({ ...t, settings: { ...t.settings, ...patch } }))}
          onName={setName}
          name={name}
          orgTone={'professional'}
        />
      </div>

      {sendTestOpen && (
        <SendTestModal
          templateId={id ?? ''}
          defaultEmail={me?.email ?? ''}
          samples={sampleList}
          defaultSample={sampleId}
          disabled={isNew}
          onClose={() => setSendTestOpen(false)}
        />
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────── */
/*  Save bar                                                            */
/* ──────────────────────────────────────────────────────────────────── */

function SaveBar({
  name,
  onName,
  saveStatus,
  onSave,
  onSaveAsNew,
  onSendTest,
  onExit,
}: {
  name: string;
  onName: (v: string) => void;
  saveStatus: 'saved' | 'saving' | 'idle' | 'error';
  onSave: () => void;
  onSaveAsNew: () => void;
  onSendTest: () => void;
  onExit: () => void;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '10px 16px',
        background: '#fff',
        border: '1px solid var(--color-border-default, #ececf2)',
        borderRadius: 8,
      }}
    >
      <button type="button" onClick={onExit} aria-label="Exit" style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 18, color: 'var(--color-text-muted)' }}>
        ←
      </button>
      <input
        type="text"
        value={name}
        onChange={(e) => onName(e.target.value)}
        placeholder="Template name"
        style={{
          flex: 1,
          padding: '6px 10px',
          border: '1px solid transparent',
          borderRadius: 6,
          fontFamily: 'inherit',
          fontSize: 16,
          fontWeight: 700,
          color: 'var(--color-text-brand, #1e1b4b)',
          background: 'transparent',
        }}
        onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--color-border-default, #ececf2)')}
        onBlur={(e) => (e.currentTarget.style.borderColor = 'transparent')}
      />
      <SaveStatusBadge status={saveStatus} />
      <button type="button" onClick={onSendTest} data-testid="send-test-btn" style={btnGhost()}>Send test</button>
      <button type="button" onClick={onSaveAsNew} style={btnGhost()}>Save as new</button>
      <button type="button" onClick={onSave} data-testid="save-btn" style={btnPrimary()}>Save</button>
    </div>
  );
}

function SaveStatusBadge({ status }: { status: 'saved' | 'saving' | 'idle' | 'error' }) {
  const map = {
    saved: { text: 'Saved', color: 'var(--color-success, #2f9e44)' },
    saving: { text: 'Saving…', color: 'var(--color-text-muted, #6b6b7d)' },
    idle: { text: '', color: 'var(--color-text-muted, #6b6b7d)' },
    error: { text: 'Save failed', color: 'var(--color-danger, #e03131)' },
  } as const;
  return <span style={{ fontSize: 12, color: map[status].color, minWidth: 60 }}>{map[status].text}</span>;
}

/* ──────────────────────────────────────────────────────────────────── */
/*  Left pane — block list                                              */
/* ──────────────────────────────────────────────────────────────────── */

function BlockListPane(props: {
  tree: TemplateBlocks;
  selected: BlockAddress | null;
  selectedRowId: string | null;
  onSelectBlock: (addr: BlockAddress) => void;
  onSelectRow: (rowId: string) => void;
  onAddRow: (layout: RowLayout) => void;
  onMoveRow: (rowId: string, dir: 'up' | 'down') => void;
  onRemoveRow: (rowId: string) => void;
  onAddBlock: (rowId: string, columnId: string, type: BlockType) => void;
  onMoveBlock: (addr: BlockAddress, dir: 'up' | 'down') => void;
  onRemoveBlock: (addr: BlockAddress) => void;
  onDuplicateBlock: (addr: BlockAddress) => void;
}) {
  return (
    <aside
      data-testid="block-list-pane"
      style={{
        background: '#fff',
        border: '1px solid var(--color-border-default, #ececf2)',
        borderRadius: 8,
        overflow: 'auto',
        padding: 12,
      }}
    >
      <h2 style={{ margin: '0 0 12px', fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.04, color: 'var(--color-text-muted)' }}>
        Structure
      </h2>
      {props.tree.rows.map((row) => (
        <div
          key={row.id}
          style={{
            marginBottom: 12,
            padding: 8,
            border: `1px solid ${props.selectedRowId === row.id ? 'var(--color-brand-blue, #3b5bdb)' : 'var(--color-border-default, #ececf2)'}`,
            borderRadius: 6,
            background: '#fafbff',
          }}
        >
          <button
            type="button"
            onClick={() => props.onSelectRow(row.id)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              padding: '4px 6px',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontSize: 11,
              fontWeight: 700,
              color: 'var(--color-text-muted)',
              textTransform: 'uppercase',
              letterSpacing: 0.04,
            }}
          >
            <span>Row · {row.layout}</span>
            <span style={{ display: 'flex', gap: 2 }}>
              <IconBtn label="Move row up" onClick={(e) => { e.stopPropagation(); props.onMoveRow(row.id, 'up'); }}>↑</IconBtn>
              <IconBtn label="Move row down" onClick={(e) => { e.stopPropagation(); props.onMoveRow(row.id, 'down'); }}>↓</IconBtn>
              <IconBtn label="Delete row" onClick={(e) => { e.stopPropagation(); props.onRemoveRow(row.id); }}>×</IconBtn>
            </span>
          </button>
          {row.columns.map((col) => (
            <div key={col.id} style={{ marginTop: 6, paddingLeft: 8 }}>
              {col.blocks.map((b) => {
                const addr: BlockAddress = { rowId: row.id, columnId: col.id, blockId: b.id };
                const isSel = props.selected?.blockId === b.id;
                return (
                  <div
                    key={b.id}
                    data-testid={`block-row-${b.id}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      padding: '4px 6px',
                      borderRadius: 4,
                      background: isSel ? 'var(--color-blue-50, #e8edff)' : 'transparent',
                      border: isSel ? '1px solid var(--color-brand-blue, #3b5bdb)' : '1px solid transparent',
                      cursor: 'pointer',
                    }}
                    onClick={() => props.onSelectBlock(addr)}
                  >
                    <span style={{ flex: 1, fontSize: 12, color: 'var(--color-text-secondary)' }}>{blockLabel(b)}</span>
                    <IconBtn label="Move up" onClick={(e) => { e.stopPropagation(); props.onMoveBlock(addr, 'up'); }}>↑</IconBtn>
                    <IconBtn label="Move down" onClick={(e) => { e.stopPropagation(); props.onMoveBlock(addr, 'down'); }}>↓</IconBtn>
                    <IconBtn label="Duplicate" onClick={(e) => { e.stopPropagation(); props.onDuplicateBlock(addr); }}>⧉</IconBtn>
                    <IconBtn label="Remove" onClick={(e) => { e.stopPropagation(); props.onRemoveBlock(addr); }}>×</IconBtn>
                  </div>
                );
              })}
              <AddBlockMenu onAdd={(type) => props.onAddBlock(row.id, col.id, type)} />
            </div>
          ))}
        </div>
      ))}
      <AddRowMenu onAdd={props.onAddRow} />
    </aside>
  );
}

function blockLabel(b: Block): string {
  switch (b.type) {
    case 'heading':
      return `H${b.props.level}: ${truncate(b.props.text)}`;
    case 'paragraph':
      return truncate(stripTags(b.props.html));
    case 'image':
      return `[Image] ${truncate(b.props.alt) || '-'}`;
    case 'button':
      return `[Button] ${b.props.label}`;
    case 'divider':
      return 'Divider';
    case 'spacer':
      return `Spacer (${b.props.height}px)`;
    case 'calendar_slot':
      return `[Calendar] ${b.props.label}`;
    case 'job_card':
      return `[Job card: {{${b.props.token}}}]`;
    case 'signature':
      return '[Signature]';
    case 'bullet_list':
      return `[List] ${b.props.items.length} items`;
    case 'detail_rows':
      return `[Detail rows] ${b.props.rows.length}`;
    case 'footer':
      return '[Footer]';
  }
}

function truncate(s: string, n = 40): string {
  return s.length <= n ? s : `${s.slice(0, n - 1)}…`;
}

function stripTags(html: string): string {
  return html.replace(/<[^>]+>/g, '').trim();
}

function IconBtn({
  children,
  onClick,
  label,
}: {
  children: React.ReactNode;
  onClick: (e: React.MouseEvent) => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      style={{
        padding: '2px 6px',
        fontSize: 11,
        background: 'transparent',
        border: '1px solid var(--color-border-default, #ececf2)',
        borderRadius: 3,
        cursor: 'pointer',
        color: 'var(--color-text-muted, #6b6b7d)',
        lineHeight: 1,
      }}
    >
      {children}
    </button>
  );
}

function AddBlockMenu({ onAdd }: { onAdd: (type: BlockType) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ marginTop: 6 }}>
      {open ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, padding: 4, background: '#fff', border: '1px solid var(--color-border-default)', borderRadius: 4 }}>
          {BLOCK_TYPES.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => { onAdd(t); setOpen(false); }}
              data-testid={`add-block-${t}`}
              style={{
                padding: '3px 8px',
                fontSize: 11,
                background: 'var(--color-surface-muted, #f7f8fc)',
                border: '1px solid var(--color-border-default)',
                borderRadius: 3,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              + {t.replace(/_/g, ' ')}
            </button>
          ))}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          style={{
            width: '100%',
            padding: '4px 6px',
            fontSize: 11,
            background: 'transparent',
            border: '1px dashed var(--color-border-default, #ececf2)',
            borderRadius: 4,
            cursor: 'pointer',
            color: 'var(--color-text-muted, #6b6b7d)',
          }}
        >
          + Add block
        </button>
      )}
    </div>
  );
}

function AddRowMenu({ onAdd }: { onAdd: (layout: RowLayout) => void }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--color-border-default, #ececf2)' }}>
      <span style={{ width: '100%', fontSize: 10, fontWeight: 700, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: 0.04, marginBottom: 4 }}>Add row</span>
      {ROW_LAYOUTS.map((l) => (
        <button
          key={l}
          type="button"
          onClick={() => onAdd(l)}
          data-testid={`add-row-${l}`}
          style={{
            padding: '4px 8px',
            fontSize: 11,
            background: '#fff',
            border: '1px solid var(--color-border-default)',
            borderRadius: 4,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          {l}
        </button>
      ))}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────── */
/*  Centre pane — preview                                               */
/* ──────────────────────────────────────────────────────────────────── */

function PreviewPane({
  html,
  viewportWidth,
  onViewportWidth,
  samples,
  sampleId,
  onSampleId,
  loading,
}: {
  html: string;
  viewportWidth: 600 | 360;
  onViewportWidth: (w: 600 | 360) => void;
  samples: { id: string; label: string }[];
  sampleId: string;
  onSampleId: (id: string) => void;
  loading: boolean;
}) {
  return (
    <section
      data-testid="preview-pane"
      style={{
        display: 'flex',
        flexDirection: 'column',
        background: '#fff',
        border: '1px solid var(--color-border-default, #ececf2)',
        borderRadius: 8,
        overflow: 'hidden',
      }}
    >
      <header style={{ padding: '10px 14px', borderBottom: '1px solid var(--color-border-default, #ececf2)', display: 'flex', gap: 12, alignItems: 'center' }}>
        <select
          value={sampleId}
          onChange={(e) => onSampleId(e.target.value)}
          style={{ padding: '4px 8px', border: '1px solid var(--color-border-default)', borderRadius: 4, fontFamily: 'inherit', fontSize: 13 }}
        >
          {samples.map((s) => (
            <option key={s.id} value={s.id}>
              {s.label}
            </option>
          ))}
        </select>
        <div role="group" style={{ display: 'inline-flex', border: '1px solid var(--color-border-default)', borderRadius: 4, overflow: 'hidden', marginLeft: 'auto' }}>
          <button type="button" onClick={() => onViewportWidth(600)} style={vpBtn(viewportWidth === 600)}>Desktop</button>
          <button type="button" onClick={() => onViewportWidth(360)} style={vpBtn(viewportWidth === 360)}>Mobile</button>
        </div>
      </header>
      <div style={{ flex: 1, padding: 16, overflow: 'auto', background: 'var(--color-surface-muted, #f7f8fc)', display: 'flex', justifyContent: 'center', alignItems: 'flex-start' }}>
        {loading && !html ? (
          <p style={{ color: 'var(--color-text-muted)' }}>Rendering preview…</p>
        ) : (
          <AutosizePreviewIframe html={html} viewportWidth={viewportWidth} />
        )}
      </div>
    </section>
  );
}

/**
 * Iframe that grows to fit its srcDoc content height. Required so the
 * outer pane's `overflow: auto` actually has something to scroll —
 * without this the iframe sits at `height: 100%` and the email body just
 * gets clipped.
 *
 * Reads `contentDocument.documentElement.scrollHeight` on load. Needs
 * `sandbox="allow-same-origin"` (NOT just `sandbox=""`) so the parent
 * can introspect the iframe document. Scripts stay blocked because
 * `allow-scripts` is NOT listed, so the relaxation is safe.
 */
function AutosizePreviewIframe({
  html,
  viewportWidth,
}: {
  html: string;
  viewportWidth: 600 | 360;
}) {
  const ref = useRef<HTMLIFrameElement | null>(null);
  const [height, setHeight] = useState(800);

  const measure = () => {
    const doc = ref.current?.contentDocument;
    if (!doc) return;
    const h = Math.max(
      doc.documentElement?.scrollHeight ?? 0,
      doc.body?.scrollHeight ?? 0,
    );
    if (h > 0) setHeight(h + 16); // small slack so the bottom border doesn't clip
  };

  return (
    <iframe
      ref={ref}
      data-testid="preview-iframe"
      title="Live preview"
      srcDoc={html}
      // Preview is visual-only — clicks blocked so sample URLs like
      // `https://blog.example/...` don't trigger DNS lookups. Wheel
      // events still pass through to the parent so the outer pane scrolls.
      // `allow-same-origin` lets us measure scrollHeight; scripts stay
      // blocked because `allow-scripts` is intentionally not listed.
      sandbox="allow-same-origin"
      onLoad={measure}
      style={{
        width: viewportWidth,
        maxWidth: '100%',
        height,
        border: '1px solid var(--color-border-default, #ececf2)',
        background: '#fff',
        pointerEvents: 'none',
      }}
    />
  );
}

function vpBtn(active: boolean): React.CSSProperties {
  return {
    padding: '4px 10px',
    background: active ? 'var(--color-action-primary, #1e1b4b)' : '#fff',
    color: active ? '#fff' : 'var(--color-text-muted)',
    border: 'none',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
  };
}

/* ──────────────────────────────────────────────────────────────────── */
/*  Right pane — settings                                               */
/* ──────────────────────────────────────────────────────────────────── */

// Token catalogue is shared with the campaign sequence editor —
// see apps/web/src/lib/merge-tokens.ts.
const CANDIDATE_SAMPLE_TOKENS = COMMON_MERGE_TOKENS;

const SPAMMY_WORDS = ['free', 'guarantee', 'act now', '$$$', 'winner', 'no obligation', 'urgent'];

function SettingsPanel({
  tree,
  selected,
  selectedRowId,
  onUpdateBlock,
  onUpdateRow,
  onChangeRowLayout,
  onUpdateSettings,
  onName,
  name,
  orgTone,
}: {
  tree: TemplateBlocks;
  selected: BlockAddress | null;
  selectedRowId: string | null;
  onUpdateBlock: (addr: BlockAddress, patch: Record<string, unknown>) => void;
  onUpdateRow: (rowId: string, patch: Record<string, unknown>) => void;
  onChangeRowLayout: (rowId: string, layout: RowLayout) => void;
  onUpdateSettings: (patch: Record<string, unknown>) => void;
  onName: (v: string) => void;
  name: string;
  orgTone: string;
}) {
  const block = selected ? findBlock(tree, selected) : null;
  const row = selectedRowId ? tree.rows.find((r) => r.id === selectedRowId) ?? null : null;

  return (
    <aside
      data-testid="settings-pane"
      style={{
        background: '#fff',
        border: '1px solid var(--color-border-default, #ececf2)',
        borderRadius: 8,
        overflow: 'auto',
        padding: 14,
      }}
    >
      {block && selected ? (
        <BlockSettings block={block} addr={selected} onUpdate={onUpdateBlock} orgTone={orgTone} />
      ) : row ? (
        <RowSettings row={row} onUpdate={onUpdateRow} onChangeLayout={onChangeRowLayout} />
      ) : (
        <TemplateSettings tree={tree} name={name} onName={onName} onUpdate={onUpdateSettings} />
      )}
    </aside>
  );
}

function BlockSettings({
  block,
  addr,
  onUpdate,
  orgTone,
}: {
  block: Block;
  addr: BlockAddress;
  onUpdate: (addr: BlockAddress, patch: Record<string, unknown>) => void;
  orgTone: string;
}) {
  const setProp = (key: string, value: unknown) => onUpdate(addr, { [key]: value });

  return (
    <div>
      <h3 style={{ margin: '0 0 8px', fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.04, color: 'var(--color-text-muted)' }}>
        {block.type.replace(/_/g, ' ')}
      </h3>
      {block.type === 'heading' && (
        <>
          <FormRow label="Text">
            <TextWithInserter value={block.props.text} onChange={(v) => setProp('text', v)} />
          </FormRow>
          <FormRow label="Level">
            <select value={block.props.level} onChange={(e) => setProp('level', Number(e.target.value))} style={inputStyle}>
              {[1, 2, 3].map((l) => <option key={l} value={l}>H{l}</option>)}
            </select>
          </FormRow>
          <AlignField value={block.props.align ?? 'left'} onChange={(v) => setProp('align', v)} />
          <ColorField label="Colour" value={block.props.color ?? ''} onChange={(v) => setProp('color', v)} />
          <LinkiActions
            currentText={block.props.text}
            orgTone={orgTone}
            onAccept={(suggestion) => setProp('text', suggestion)}
          />
        </>
      )}

      {block.type === 'paragraph' && (
        <>
          <FormRow label="HTML">
            <TextWithInserter value={block.props.html} onChange={(v) => setProp('html', v)} textarea />
          </FormRow>
          <AlignField value={block.props.align ?? 'left'} onChange={(v) => setProp('align', v)} />
          <ColorField label="Colour" value={block.props.color ?? ''} onChange={(v) => setProp('color', v)} />
          <LinkiActions
            currentText={block.props.html}
            orgTone={orgTone}
            onAccept={(suggestion) => setProp('html', suggestion)}
          />
        </>
      )}

      {block.type === 'button' && (
        <>
          <FormRow label="Label"><TextWithInserter value={block.props.label} onChange={(v) => setProp('label', v)} /></FormRow>
          <FormRow label="URL"><TextWithInserter value={block.props.href} onChange={(v) => setProp('href', v)} /></FormRow>
          <ColorField label="Background" value={block.props.background ?? ''} onChange={(v) => setProp('background', v)} />
          <ColorField label="Text colour" value={block.props.color ?? ''} onChange={(v) => setProp('color', v)} />
          <AlignField value={block.props.align ?? 'left'} onChange={(v) => setProp('align', v)} />
        </>
      )}

      {block.type === 'image' && (
        <>
          <FormRow label="Image">
            <ImageUploadField value={block.props.src} onChange={(url) => setProp('src', url ?? '')} testId="template-image" />
          </FormRow>
          <FormRow label="Alt text"><TextWithInserter value={block.props.alt} onChange={(v) => setProp('alt', v)} /></FormRow>
          <FormRow label="Link (optional)"><TextWithInserter value={block.props.href ?? ''} onChange={(v) => setProp('href', v)} /></FormRow>
          <FormRow label="Rounded corners">
            <input type="checkbox" checked={block.props.rounded ?? false} onChange={(e) => setProp('rounded', e.target.checked)} />
          </FormRow>
        </>
      )}

      {block.type === 'spacer' && (
        <FormRow label="Height (px)">
          <input type="number" value={block.props.height} min={4} max={120} onChange={(e) => setProp('height', Number(e.target.value))} style={inputStyle} />
        </FormRow>
      )}

      {block.type === 'divider' && (
        <>
          <ColorField label="Colour" value={block.props.color ?? ''} onChange={(v) => setProp('color', v)} />
          <FormRow label="Thickness (px)">
            <input type="number" value={block.props.thickness ?? 1} min={1} max={8} onChange={(e) => setProp('thickness', Number(e.target.value))} style={inputStyle} />
          </FormRow>
        </>
      )}

      {block.type === 'calendar_slot' && (
        <>
          <FormRow label="Calendar (pick from your booking links or paste a URL)">
            <CalendarSlotPicker value={block.props.url} onChange={(v) => setProp('url', v)} />
          </FormRow>
          <FormRow label="Button label"><TextWithInserter value={block.props.label} onChange={(v) => setProp('label', v)} /></FormRow>
          <FormRow label="Description (optional)"><TextWithInserter value={block.props.description ?? ''} onChange={(v) => setProp('description', v)} /></FormRow>
        </>
      )}

      {block.type === 'job_card' && (
        <>
          <FormRow label="Token (e.g. 'job', 'job_1')">
            <input type="text" value={block.props.token} onChange={(e) => setProp('token', e.target.value)} style={inputStyle} />
          </FormRow>
          <FormRow label="Show hero image">
            <input type="checkbox" checked={block.props.show_image ?? false} onChange={(e) => setProp('show_image', e.target.checked)} />
          </FormRow>
          <FormRow label="Show salary">
            <input type="checkbox" checked={block.props.show_salary ?? false} onChange={(e) => setProp('show_salary', e.target.checked)} />
          </FormRow>
          <FormRow label="Show summary">
            <input type="checkbox" checked={block.props.show_summary ?? false} onChange={(e) => setProp('show_summary', e.target.checked)} />
          </FormRow>
        </>
      )}

      {block.type === 'signature' && (
        <>
          <FormRow label="Include title">
            <input type="checkbox" checked={block.props.include_title !== false} onChange={(e) => setProp('include_title', e.target.checked)} />
          </FormRow>
          <FormRow label="Include phone">
            <input type="checkbox" checked={block.props.include_phone !== false} onChange={(e) => setProp('include_phone', e.target.checked)} />
          </FormRow>
        </>
      )}

      {block.type === 'bullet_list' && (
        <FormRow label="Items (one per line)">
          <BulletListItemsField
            items={block.props.items}
            onChange={(items) => setProp('items', items)}
          />
        </FormRow>
      )}

      {block.type === 'footer' && (
        <p style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
          Footer auto-renders company name, address, and the {`{{unsubscribe_url}}`} link.
          Set company address + footer overrides under Settings → Brand kit.
        </p>
      )}
    </div>
  );
}

function RowSettings({
  row,
  onUpdate,
  onChangeLayout,
}: {
  row: TemplateBlocks['rows'][number];
  onUpdate: (rowId: string, patch: Record<string, unknown>) => void;
  onChangeLayout: (rowId: string, layout: RowLayout) => void;
}) {
  return (
    <div>
      <h3 style={{ margin: '0 0 8px', fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.04, color: 'var(--color-text-muted)' }}>
        Row
      </h3>
      <FormRow label="Layout">
        <select value={row.layout} onChange={(e) => onChangeLayout(row.id, e.target.value as RowLayout)} style={inputStyle}>
          {ROW_LAYOUTS.map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
      </FormRow>
      <ColorField label="Background" value={row.background ?? ''} onChange={(v) => onUpdate(row.id, { background: v })} />
    </div>
  );
}

function TemplateSettings({
  tree,
  name,
  onName,
  onUpdate,
}: {
  tree: TemplateBlocks;
  name: string;
  onName: (v: string) => void;
  onUpdate: (patch: Record<string, unknown>) => void;
}) {
  const text = useMemo(() => {
    return tree.rows
      .flatMap((r) => r.columns.flatMap((c) => c.blocks))
      .map((b) => {
        if (b.type === 'paragraph') return stripTags(b.props.html);
        if (b.type === 'heading') return b.props.text;
        if (b.type === 'button') return b.props.label;
        return '';
      })
      .join(' ');
  }, [tree]);

  const spamHits = useMemo(
    () => SPAMMY_WORDS.filter((w) => new RegExp(`\\b${w.replace(/[.$]/g, '\\$&')}\\b`, 'i').test(text)),
    [text],
  );
  const linkCount = useMemo(() => {
    let n = 0;
    for (const r of tree.rows) for (const c of r.columns) for (const b of c.blocks) {
      if (b.type === 'button' || b.type === 'calendar_slot') n++;
      if (b.type === 'image' && b.props.href) n++;
    }
    return n;
  }, [tree]);
  const hasUnsub = useMemo(
    () => tree.rows.some((r) => r.columns.some((c) => c.blocks.some((b) => b.type === 'footer'))),
    [tree],
  );

  return (
    <div>
      <h3 style={{ margin: '0 0 8px', fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.04, color: 'var(--color-text-muted)' }}>
        Template
      </h3>
      <FormRow label="Name">
        <input type="text" value={name} onChange={(e) => onName(e.target.value)} style={inputStyle} />
      </FormRow>
      <FormRow label="Subject"><TextWithInserter value={tree.settings.subject} onChange={(v) => onUpdate({ subject: v })} /></FormRow>
      <FormRow label="Preheader"><TextWithInserter value={tree.settings.preheader ?? ''} onChange={(v) => onUpdate({ preheader: v })} /></FormRow>
      <ColorField label="Background" value={tree.settings.background_color ?? ''} onChange={(v) => onUpdate({ background_color: v })} />
      <ColorField label="Link colour" value={tree.settings.link_color ?? ''} onChange={(v) => onUpdate({ link_color: v })} />
      <ColorField label="Heading colour" value={tree.settings.heading_color ?? ''} onChange={(v) => onUpdate({ heading_color: v })} />
      <FormRow label="Button radius (px)">
        <input type="number" value={tree.settings.button_radius_px ?? 8} min={0} max={40} onChange={(e) => onUpdate({ button_radius_px: Number(e.target.value) })} style={inputStyle} />
      </FormRow>
      <FormRow label="Content width (px)">
        <select value={tree.settings.content_width_px ?? 600} onChange={(e) => onUpdate({ content_width_px: Number(e.target.value) })} style={inputStyle}>
          <option value={560}>560</option>
          <option value={600}>600</option>
          <option value={640}>640</option>
        </select>
      </FormRow>

      <hr style={{ margin: '14px 0', border: 'none', borderTop: '1px solid var(--color-border-default)' }} />

      <h3 style={{ margin: '0 0 8px', fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.04, color: 'var(--color-text-muted)' }}>
        Deliverability
      </h3>
      <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 4 }}>
        <DeliverabilityRow ok={spamHits.length === 0} label={`Spammy words: ${spamHits.length === 0 ? '0 found' : `${spamHits.length} (${spamHits.join(', ')})`}`} />
        <DeliverabilityRow ok={linkCount <= 6} label={`Link count: ${linkCount}`} />
        <DeliverabilityRow ok={hasUnsub} label={hasUnsub ? 'Unsubscribe present' : 'Unsubscribe footer auto-injected on save'} />
      </ul>
    </div>
  );
}

function DeliverabilityRow({ ok, label }: { ok: boolean; label: string }) {
  return (
    <li style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: ok ? 'var(--color-success, #2f9e44)' : 'var(--color-warning, #d97706)' }}>
      <span aria-hidden="true">{ok ? '●' : '!'}</span>
      <span>{label}</span>
    </li>
  );
}

function FormRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 10 }}>
      <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)' }}>{label}</span>
      {children}
    </label>
  );
}

const inputStyle: React.CSSProperties = {
  padding: '6px 10px',
  border: '1px solid var(--color-border-default, #ececf2)',
  borderRadius: 4,
  fontSize: 13,
  fontFamily: 'inherit',
};

function ColorField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <FormRow label={label}>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <input type="color" value={value || '#1e1b4b'} onChange={(e) => onChange(e.target.value)} style={{ width: 36, height: 30, padding: 0, border: '1px solid var(--color-border-default)', borderRadius: 4 }} />
        <input type="text" value={value} onChange={(e) => onChange(e.target.value)} placeholder="inherit" style={{ ...inputStyle, flex: 1 }} />
        {value && (
          <button type="button" onClick={() => onChange('')} style={{ padding: '3px 8px', fontSize: 11, border: '1px solid var(--color-border-default)', borderRadius: 4, background: '#fff', cursor: 'pointer' }}>
            Clear
          </button>
        )}
      </div>
    </FormRow>
  );
}

function AlignField({ value, onChange }: { value: 'left' | 'center' | 'right'; onChange: (v: 'left' | 'center' | 'right') => void }) {
  return (
    <FormRow label="Align">
      <div style={{ display: 'inline-flex', border: '1px solid var(--color-border-default)', borderRadius: 4, overflow: 'hidden' }}>
        {(['left', 'center', 'right'] as const).map((a) => (
          <button
            key={a}
            type="button"
            onClick={() => onChange(a)}
            style={{
              padding: '4px 10px',
              fontSize: 11,
              background: value === a ? 'var(--color-action-primary, #1e1b4b)' : '#fff',
              color: value === a ? '#fff' : 'var(--color-text-muted)',
              border: 'none',
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            {a}
          </button>
        ))}
      </div>
    </FormRow>
  );
}

/** Text input/textarea with an inline Merge-field inserter. */
function TextWithInserter({
  value,
  onChange,
  textarea,
}: {
  value: string;
  onChange: (v: string) => void;
  textarea?: boolean;
}) {
  const [caret, setCaret] = useState(0);
  return (
    <div style={{ display: 'flex', gap: 4, alignItems: 'flex-start' }}>
      {textarea ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onSelect={(e) => setCaret(e.currentTarget.selectionStart ?? value.length)}
          rows={4}
          style={{ ...inputStyle, flex: 1, minHeight: 90, resize: 'vertical' }}
        />
      ) : (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onSelect={(e) => setCaret(e.currentTarget.selectionStart ?? value.length)}
          style={{ ...inputStyle, flex: 1 }}
        />
      )}
      <MergeFieldInserter tokens={CANDIDATE_SAMPLE_TOKENS} onInsert={onChange} value={value} caret={caret} />
    </div>
  );
}

/**
 * Bullet-list items editor. The textarea holds raw text (one item per
 * line) in LOCAL state so hitting Enter creates a new line without the
 * empty-line filter stripping it mid-keystroke. On blur, lines are
 * trimmed + non-empty lines committed back to the block as the items
 * array. While typing, the parent tree isn't updated — keeps the cursor
 * stable and the input fast.
 */
function BulletListItemsField({
  items,
  onChange,
}: {
  items: string[];
  onChange: (next: string[]) => void;
}) {
  const [draft, setDraft] = useState(items.join('\n'));
  // Sync from props when the parent replaces items wholesale (e.g.
  // selecting a different block of the same type). Avoid clobbering the
  // user's in-progress edits when the round-trip is just our own commit.
  useEffect(() => {
    const next = items.join('\n');
    const trimmed = draft.split('\n').map((s) => s.trim()).filter(Boolean).join('\n');
    if (next !== trimmed) setDraft(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const commit = () => {
    const parsed = draft.split('\n').map((s) => s.trim()).filter(Boolean);
    onChange(parsed);
  };

  return (
    <textarea
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      rows={5}
      placeholder={'First point\nSecond point\nThird point'}
      style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }}
    />
  );
}

/* ──────────────────────────────────────────────────────────────────── */
/*  Send test modal                                                     */
/* ──────────────────────────────────────────────────────────────────── */

function SendTestModal({
  templateId,
  defaultEmail,
  samples,
  defaultSample,
  disabled,
  onClose,
}: {
  templateId: string;
  defaultEmail: string;
  samples: { id: string; label: string }[];
  defaultSample: string;
  disabled: boolean;
  onClose: () => void;
}) {
  const [to, setTo] = useState(defaultEmail);
  const [sample, setSample] = useState(defaultSample);
  const mut = useSendTestTemplate(templateId);
  const { addToast } = useToast();

  const handleSend = async () => {
    try {
      await mut.mutateAsync({ to, sample_id: sample });
      addToast({ variant: 'success', title: `Test sent to ${to}` });
      onClose();
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Send failed';
      addToast({ variant: 'danger', title: 'Test send failed', description: msg });
    }
  };

  return (
    <div role="dialog" aria-modal="true" onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, padding: 24, width: 420, maxWidth: '90vw' }}>
        <h2 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 700, color: 'var(--color-text-brand)' }}>Send test</h2>
        {disabled && (
          <p style={{ color: 'var(--color-warning, #d97706)', fontSize: 12, marginBottom: 8 }}>
            Save the template first. Test sends need a saved template.
          </p>
        )}
        <FormRow label="To">
          <input type="email" value={to} onChange={(e) => setTo(e.target.value)} style={inputStyle} disabled={disabled} />
        </FormRow>
        <FormRow label="Sample">
          <select value={sample} onChange={(e) => setSample(e.target.value)} style={inputStyle} disabled={disabled}>
            {samples.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </FormRow>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
          <button type="button" onClick={onClose} style={btnGhost()}>Cancel</button>
          <button type="button" onClick={handleSend} disabled={disabled || mut.isPending} style={btnPrimary()}>
            {mut.isPending ? 'Sending…' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── tiny shared styles ── */
function btnPrimary(): React.CSSProperties {
  return {
    padding: '6px 14px',
    fontSize: 13,
    fontWeight: 600,
    background: 'var(--color-action-primary, #1e1b4b)',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer',
    fontFamily: 'inherit',
  };
}
function btnGhost(): React.CSSProperties {
  return {
    padding: '6px 14px',
    fontSize: 13,
    fontWeight: 600,
    background: '#fff',
    color: 'var(--color-text-secondary, #2b2b3c)',
    border: '1px solid var(--color-border-default, #ececf2)',
    borderRadius: 6,
    cursor: 'pointer',
    fontFamily: 'inherit',
  };
}
